import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Table, useLocalizeMessage, Toggle } from 'libs.nucleus';
import { PageHeader, useBooleanState } from 'libs.react';
import { useDataViewsColumns } from './data_views_columns.hook';
import { useDataViews } from '../../hooks';
import { CreateViewModal } from '../create_view_modal/create_view_modal.component';
export const DataViewsDashboard = () => {
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const [isCreateViewModalOpen, openCreateViewModal, closeCreateViewModal] = useBooleanState();
    const [showArchived, setShowArchived] = useState(false);
    const [dataViews, setDataViews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { fetchDataViews, archiveDataView, isLoading: isApiLoading } = useDataViews();
    const handleArchiveView = useCallback(async (view) => {
        try {
            await archiveDataView(view);
            // Reload views after archiving
            setIsLoading(true);
            try {
                const views = await fetchDataViews(showArchived);
                setDataViews(views);
            }
            catch (error) {
                // Error already handled in hook
                setDataViews([]);
            }
            finally {
                setIsLoading(false);
            }
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [archiveDataView, fetchDataViews, showArchived]);
    const dataViewsColumns = useDataViewsColumns({
        archiveView: handleArchiveView,
    });
    useEffect(() => {
        let isMounted = true;
        const loadDataViews = async () => {
            setIsLoading(true);
            try {
                // Fetch views based on showArchived toggle - filtering is done server-side
                const views = await fetchDataViews(showArchived);
                if (isMounted) {
                    setDataViews(views);
                }
            }
            catch (error) {
                // Error already handled in hook
                if (isMounted) {
                    setDataViews([]);
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadDataViews();
        return () => {
            isMounted = false;
        };
    }, [fetchDataViews, showArchived]); // Refetch when showArchived toggle changes
    const handleCreateViewSuccess = useCallback((view) => {
        closeCreateViewModal();
        navigate(`/library/data-views/${view.id}`);
    }, [closeCreateViewModal, navigate]);
    const hasNoResults = !isLoading && !isApiLoading && dataViews.length === 0;
    const headerOptions = [
        {
            name: translate('Create dataset'),
            onClick: openCreateViewModal,
            variant: 'primary',
            disabled: isLoading || isApiLoading,
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Datasets'), subtitle: translate('Create and manage custom workspace views with dimensions and measures.'), LinkComponent: Link, options: headerOptions }), _jsx(Table, { isLoading: isLoading || isApiLoading, data: dataViews, columns: dataViewsColumns, labelNoResults: hasNoResults ? translate('No datasets found') : translate('No datasets found'), useInternalSearch: true, enableSorting: true, labelSearchField: translate('Search'), customHeaderContent: _jsx("div", { className: 'flex gap-4 ml-auto', children: _jsx("div", { className: 'border-border-primary border-1 rounded-lg py-2 px-4', children: _jsx(Toggle, { label: translate('Show archived'), checked: showArchived, onChange: setShowArchived }) }) }) }), isCreateViewModalOpen && _jsx(CreateViewModal, { onClose: closeCreateViewModal, onSuccess: handleCreateViewSuccess })] }));
};
